import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    FormControlLabel,
    Switch,
    Grid,
    Divider,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Paper,
    Card,
    CardContent,
    CardHeader,
} from '@mui/material';
import { Scenario, ScenarioTool, getScenario, createScenario, updateScenario, getValuePools, getScenarioScopes } from '../../api/scenarios';
import { getContractList } from '../../api/api';
import ReferencesTable from '../../components/ReferencesTable';
import { SelectChangeEvent } from '@mui/material/Select';

const ScenarioDetail: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    
    // Get the tab value from URL search params if available
    const searchParams = new URLSearchParams(window.location.search);
    const tabValue = searchParams.get('tab') || '0';
    const isNew = id === 'new';
    const [scenario, setScenario] = useState<Partial<Scenario>>({
        scenario_name: '',
        question: '',
        scope: '',
        group: 1,
        sequence: 1,
        is_active: true,
        retrieval_class: 'retrieval.opportunity.OpportunityRetriever',
        contract_id: null,
        tools: [
            { name: 'Maths', enabled: false },
            { name: 'World Bank Commodity Price Data', enabled: false }
        ],
    });
    const [contractWorkspaces, setContractWorkspaces] = useState<Array<{contract_id: number, contract_workspace: string}>>([]);
    const [valuePools, setValuePools] = useState<string[]>([]);
    const [scenarioScopes, setScenarioScopes] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Load contract workspaces
        loadContractWorkspaces();
        // Load value pools
        loadValuePools();
        // Load scenario scopes
        loadScenarioScopes();
        
        if (!isNew && id) {
            loadScenario(Number(id));
        }
    }, [id]);
    
    const loadContractWorkspaces = async () => {
        try {
            const response = await getContractList('');
            if (response && response.data && response.data.contracts) {
                setContractWorkspaces(response.data.contracts);
            }
        } catch (error) {
            console.error('Error loading contract workspaces:', error);
        }
    };

    const loadValuePools = async () => {
        try {
            const pools = await getValuePools();
            setValuePools(pools);
        } catch (error) {
            console.error('Error loading value pools:', error);
        }
    };

    const loadScenarioScopes = async () => {
        try {
            const scopes = await getScenarioScopes();
            setScenarioScopes(scopes);
        } catch (error) {
            console.error('Error loading scenario scopes:', error);
        }
    };

    const loadScenario = async (scenarioId: number) => {
        try {
            const data = await getScenario(scenarioId);
            // Ensure we have the scenario data loaded
            setScenario(data);
            
            // Make sure contract workspaces are loaded
            if (contractWorkspaces.length === 0) {
                await loadContractWorkspaces();
            }
        } catch (error) {
            console.error('Error loading scenario:', error);
            navigate('/homepage/scenarios');
        }
    };

    // Handler for text fields
    const handleTextFieldChange = (field: keyof Scenario) => (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const value = event.target.value;
        setScenario((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // Handler for checkbox fields
    const handleCheckboxChange = (field: keyof Scenario) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.checked;
        setScenario((prev) => ({
            ...prev,
            [field]: value,
        }));
    };
    
    // Handler for toggling tool switches
    const handleToolToggle = (toolName: string) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const isEnabled = event.target.checked;
        setScenario((prev) => {
            // Create a copy of the current tools array
            const updatedTools = prev.tools ? [...prev.tools] : [];
            
            // Find the tool by name and update its enabled status
            const toolIndex = updatedTools.findIndex(tool => tool.name === toolName);
            
            if (toolIndex !== -1) {
                // Update existing tool
                updatedTools[toolIndex] = {
                    ...updatedTools[toolIndex],
                    enabled: isEnabled
                };
            } else {
                // Add new tool if it doesn't exist
                updatedTools.push({
                    name: toolName,
                    enabled: isEnabled
                });
            }
            
            return {
                ...prev,
                tools: updatedTools
            };
        });
    };

    // Handler for select fields
    const handleSelectChange = (field: keyof Scenario) => (
        event: SelectChangeEvent<string | number>
    ) => {
        let value = event.target.value;
        
        // Handle contract_id special case - convert empty string to null
        if (field === 'contract_id' && value === '') {
            value = null as any;
        }
        
        // Handle contract_id special case - convert string to number
        if (field === 'contract_id' && value !== null && value !== '') {
            value = Number(value) as any;
        }
        
        setScenario((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        
        // Create a filtered copy of the scenario with only the expected fields
        const scenarioToSubmit = {
            scenario_name: scenario.scenario_name,
            question: scenario.question,
            scope: scenario.scope,
            group: scenario.group,
            sequence: scenario.sequence,
            is_active: scenario.is_active,
            retrieval_class: scenario.retrieval_class,
            prompt_name: scenario.prompt_name,
            contract_id: scenario.contract_id,
            tools: scenario.tools,
            category: scenario.category
        };
        
        // Ensure contract_id is properly formatted (null or number)
        if (!scenarioToSubmit.contract_id) {
            scenarioToSubmit.contract_id = null;
        } else if (scenarioToSubmit.contract_id !== null) {
            scenarioToSubmit.contract_id = Number(scenarioToSubmit.contract_id);
        }
        
        console.log('Submitting scenario:', scenarioToSubmit);
        
        try {
            // Save the scenario
            await (isNew ? createScenario(scenarioToSubmit) : updateScenario(Number(id), scenarioToSubmit));
            
            // Simple approach: if scenario has a contract_id, go to Contract tab (1), otherwise Generic tab (0)
            const returnTab = scenarioToSubmit.contract_id ? '1' : '0';
            
            // Navigate back to scenarios page with the appropriate tab selected
            navigate(`/homepage/scenarios?tab=${returnTab}`);
        } catch (error) {
            console.error('Error saving scenario:', error);
        }
    };

    return (
        <Grid
            container
            sx={{ width: "100%", justifyContent: "center", minHeight: "95vh", pb: 8 }}
        >
            <Grid
                sx={{
                    flex: 1,
                    mt: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    background: "#fff",
                    boxShadow:
                        "0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
                    borderRadius: "8px",
                    width: "100%",
                }}
            >
                <Grid
                    container
                    sx={{ width: "100%" }}
                    justifyContent="space-between"
                >
                    <Typography
                        align="center"
                        sx={{ fontSize: "1.5rem", px: 2, p: 1.5 }}
                    >
                        {isNew ? 'Create New Scenario' : 'Edit Scenario'}
                    </Typography>
                    <Divider sx={{ width: "100%" }} />
                </Grid>

                <Grid container spacing={2} sx={{ p: 1.5, width: '100%' }}>
                    <Grid item xs={12}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={4}>
                                {/* General Information Section */}
                                <Grid item xs={12}>
                                    <Paper elevation={2} sx={{ p: 3 }}>
                                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>General Information</Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined" // Ensure outlined variant
                                                    label="Scenario Name"
                                                    value={scenario.scenario_name}
                                                    onChange={handleTextFieldChange('scenario_name')}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined" // Ensure outlined variant
                                                    label="Question"
                                                    value={scenario.question}
                                                    onChange={handleTextFieldChange('question')}
                                                    required
                                                    multiline
                                                    rows={4}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Scenario Configuration Section */}
                                <Grid item xs={12}>
                                    <Paper elevation={2} sx={{ p: 3 }}>
                                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Scenario Configuration</Typography>
                                        <Grid container spacing={3} alignItems="center"> {/* Align items for Switch */}
                                             {/* Contract Workspace and Value Pool Side-by-side */}
                                             <Grid item xs={12} sm={6}> {/* Always 6 columns on sm+ */}
                                                 <FormControl fullWidth>
                                                     <InputLabel id="contract-workspace-label">Contract Workspace</InputLabel>
                                                     <Select
                                                         labelId="contract-workspace-label"
                                                         value={scenario.contract_id || ''}
                                                         onChange={handleSelectChange('contract_id') as (event: SelectChangeEvent<number>) => void}
                                                         label="Contract Workspace"
                                                         renderValue={(selected) => {
                                                             if (!selected) return "None (Generic Scenario)";
                                                             const workspace = contractWorkspaces.find(w => w.contract_id === selected);
                                                             return workspace ? workspace.contract_workspace : `Contract ID: ${selected}`;
                                                         }}
                                                     >
                                                         <MenuItem value="">None (Generic Scenario)</MenuItem>
                                                         {contractWorkspaces.map((workspace) => (
                                                             <MenuItem key={workspace.contract_id} value={workspace.contract_id}>
                                                                 {workspace.contract_workspace}
                                                             </MenuItem>
                                                         ))}
                                                     </Select>
                                                 </FormControl>
                                             </Grid>
                                             {/* Value Pool dropdown - always visible */}
                                             <Grid item xs={12} sm={6}>
                                                 <FormControl fullWidth> {/* Always enabled */}
                                                     <InputLabel id="value-pool-label">Value Pool</InputLabel>
                                                     <Select
                                                         labelId="value-pool-label"
                                                         value={scenario.category || ''} // Use empty string for 'None'
                                                         onChange={handleSelectChange('category')}
                                                         label="Value Pool"
                                                     >
                                                         <MenuItem value=""><em>None</em></MenuItem> 
                                                         {valuePools.map((pool) => (
                                                             <MenuItem key={pool} value={pool}>
                                                                 {pool}
                                                             </MenuItem>
                                                         ))}
                                                     </Select>
                                                 </FormControl>
                                             </Grid>
                                             <Grid item xs={12}>
                                                 <FormControlLabel
                                                     control={
                                                         <Switch
                                                             checked={!!scenario.is_active} // Ensure boolean value
                                                             onChange={handleCheckboxChange('is_active')}
                                                         />
                                                     }
                                                     label="Active Scenario"
                                                 />
                                             </Grid>
                                             <Grid item xs={12} sm={6}>
                                                 <FormControl fullWidth>
                                                     <InputLabel id="scope-label">Scope</InputLabel>
                                                     <Select
                                                         labelId="scope-label"
                                                         value={scenario.scope || ''}
                                                         onChange={handleSelectChange('scope')}
                                                         label="Scope"
                                                     >
                                                         <MenuItem value=""><em>None</em></MenuItem> 
                                                         {scenarioScopes.map((scope) => (
                                                             <MenuItem key={scope} value={scope}>
                                                                 {scope}
                                                             </MenuItem>
                                                         ))}
                                                     </Select>
                                                 </FormControl>
                                             </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Developer Settings Section */}
                                <Grid item xs={12}>
                                    <Paper elevation={2} sx={{ p: 3 }}>
                                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Developer Settings</Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined" // Ensure outlined variant
                                                    label="Group"
                                                    type="number"
                                                    value={scenario.group}
                                                    onChange={handleTextFieldChange('group')}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined" // Ensure outlined variant
                                                    label="Sequence"
                                                    type="number"
                                                    value={scenario.sequence}
                                                    onChange={handleTextFieldChange('sequence')}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined" // Ensure outlined variant
                                                    label="Retrieval Class"
                                                    value={scenario.retrieval_class}
                                                    onChange={handleTextFieldChange('retrieval_class')}
                                                    required
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Available Tools Section - Refactored */}
                                <Grid item xs={12}>
                                    <Paper elevation={2} sx={{ p: 3 }}>
                                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Available Tools</Typography>
                                        <Grid container spacing={1}> {/* Removed padding p:1 from here */}
                                            {scenario.tools?.map((tool, index) => (
                                                <Grid item xs={12} sm={6} md={4} lg={3} key={tool.name}> {/* Adjusted grid sizing */}
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={tool.enabled}
                                                                onChange={handleToolToggle(tool.name)}
                                                                color="primary"
                                                            />
                                                        }
                                                        label={tool.name}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Paper>
                                </Grid>
                                
                                {/* Show references table if not a new scenario and references exist */}
                                {!isNew && scenario.references && scenario.references.length > 0 && (
                                    <Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            References
                                        </Typography>
                                        <ReferencesTable 
                                            references={scenario.references}
                                            title="Scenario References"
                                        />
                                    </Grid>
                                )}
                                
                                <Grid item xs={12}>
                                    <Box display="flex" gap={2} justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigate(`/homepage/scenarios?tab=${scenario.contract_id ? '1' : '0'}`)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button type="submit" variant="contained" color="primary">
                                            {isNew ? 'Create' : 'Save'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default ScenarioDetail;
